import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, getBearer } from "../../lib/api";
import { extractError } from "../../lib/format";
import { Loader2, Download } from "lucide-react";

function formatWhen(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminWaitlist() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [source, setSource] = useState("all");
  const [tier, setTier] = useState("all");

  useEffect(() => {
    let alive = true;
    api.get("/waitlist?limit=500")
      .then(({ data }) => alive && setState({ loading: false, data, error: null }))
      .catch((e) => alive && setState({ loading: false, data: null, error: extractError(e, "Could not load waitlist.") }));
    return () => { alive = false; };
  }, []);

  const items = state.data?.items || [];
  const sources = useMemo(() => Array.from(new Set(items.map((i) => i.source).filter(Boolean))), [items]);
  const tiers = useMemo(() => Array.from(new Set(items.map((i) => i.preferred_tier).filter(Boolean))), [items]);
  const filtered = items.filter((i) =>
    (source === "all" || i.source === source) &&
    (tier === "all" || i.preferred_tier === tier)
  );

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/waitlist.csv`, {
        headers: { Authorization: `Bearer ${getBearer()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `waitlist_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e, "Could not download CSV."));
    }
  };

  return (
    <div className="ftg-adm__stack" data-testid="waitlist-root">
      <div className="ftg-adm__toolbar">
        <label className="ftg-adm__filter">
          <span>Source</span>
          <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="wl-filter-source">
            <option value="all">All sources</option>
            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="ftg-adm__filter">
          <span>Tier</span>
          <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="wl-filter-tier">
            <option value="all">All tiers</option>
            {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <div className="ftg-adm__toolbar-spacer" />
        <button type="button" onClick={downloadCsv} className="ftg-btn ftg-btn--primary ftg-btn--sm" data-testid="wl-download-csv">
          <Download size={14} /> Download CSV
        </button>
      </div>

      {state.loading && <div className="ftg-adm__empty" data-testid="wl-loading"><Loader2 className="ftg-spin" size={16} /> Loading…</div>}
      {state.error && <div className="ftg-adm__empty">{state.error}</div>}
      {state.data && (
        <div className="ftg-adm__table-wrap">
          <table className="ftg-adm__table" data-testid="waitlist-table">
            <thead>
              <tr><th>Email</th><th>Source</th><th>Tier</th><th>Country</th><th>Age band interest</th><th>Joined</th></tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`wl-row-${r.id}`}>
                  <td className="ftg-adm__mono">{r.email}</td>
                  <td>{r.source || "\u2014"}</td>
                  <td><span className={`ftg-adm__pill${r.preferred_tier === "founder" ? " is-paid" : ""}`}>{r.preferred_tier}</span></td>
                  <td>{r.country || "\u2014"}</td>
                  <td>{r.age_band_interest || "\u2014"}</td>
                  <td className="ftg-adm__nowrap">{formatWhen(r.created_at)}</td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={6} className="ftg-adm__muted">No rows match the filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {state.data && (
        <div className="ftg-adm__pager-info">
          Showing {filtered.length} of {state.data.total} total signups
        </div>
      )}
    </div>
  );
}
